import React, { useState } from "react";
import "./BattleInventory.css";
import {
  potionImg,
  superPotionImg,
  hyperPotionImg,
  maxPotionImg,
} from "../../../constants/items";

// Import revive images (assuming they exist)
const reviveImg = "/items/revive.png";
const maxReviveImg = "/items/maxrevive.png";
const revivalHerbImg = "/items/revivalherb.png";
const energyRootImg = "/items/energyroot.png";

// Map of item IDs to images and names
const ITEM_MAP = {
  29: { img: potionImg, alt: "Potion", description: "Restores 20 HP" },
  30: { img: superPotionImg, alt: "Super Potion", description: "Restores 50 HP" },
  31: { img: hyperPotionImg, alt: "Hyper Potion", description: "Restores 200 HP" },
  32: { img: maxPotionImg, alt: "Max Potion", description: "Fully restores HP" },
  84: { img: reviveImg, alt: "Revive", description: "Revives fainted Pokémon with half HP" },
  85: { img: maxReviveImg, alt: "Max Revive", description: "Revives fainted Pokémon with full HP" },
  170: { img: revivalHerbImg, alt: "Revival Herb", description: "Revives fainted Pokémon with full HP" },
  171: { img: energyRootImg, alt: "Energy Root", description: "Restores 200 HP" },
};

export default function BattleInventoryPanel({
  inventory,
  selectedAction,
  setSelectedAction,
  onClose,
  roster = [],
  onItemUse,
  battleType = "wild" // Add battleType prop with default value
}) {
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectingTarget, setSelectingTarget] = useState(false);

  // Check if items are disabled based on battle type
  const itemsDisabled = battleType === "60second" || battleType === "endless";
  
  // Render an item with its image and count
  const renderItem = item => {
    const isSel = selectedItem === item.id;
    const itemData = ITEM_MAP[item.id] || { img: potionImg, alt: `Item ${item.id}` };
    
    const onClick = () => {
      // Don't allow item selection if items are disabled
      if (itemsDisabled) return;
      
      setSelectedItem(item.id);
      setSelectingTarget(true);
    };

    // Don't display items with zero quantity
    if (item.amount <= 0) return null;

    return (
      <div
        key={item.id}
        onClick={onClick}
        className={`battle-inventory-item ${isSel ? 'selected' : ''} ${itemsDisabled ? 'disabled' : ''}`}
      >
        <img
          src={itemData.img}
          alt={itemData.alt}
          className="battle-inventory-item-img"
        />
        <div className="battle-inventory-item-count">
          {item.amount}
        </div>
        {itemsDisabled && (
          <div className="battle-inventory-disabled-overlay">
            <span className="battle-inventory-disabled-text">Items disabled in Battle Tower</span>
          </div>
        )}
      </div>
    );
  };

  // Render Pokémon selection for item target
  const renderPokemonSelection = () => {
    const isReviveItem = selectedItem && [84, 85, 170].includes(selectedItem);
    const isPotionItem = selectedItem && [29, 30, 31, 32, 171].includes(selectedItem);
    
    return (
      <div className="pokemon-target-selection">

        <div className="pokemon-target-list">
          {roster.map(pokemon => {
            const fainted = pokemon.current_hp <= 0;
            const hpPercent = pokemon.current_hp / pokemon.max_hp * 100;
            const hpColor = hpPercent <= 25 ? '#dc3545' : 
                          hpPercent <= 50 ? '#ffc107' : '#28a745';
            
            // Determine if this Pokemon can be targeted with the selected item
            let canUseItem = false;
            let disabledReason = "";
            
            if (isReviveItem) {
              canUseItem = fainted;
              disabledReason = fainted ? "" : "Revive items can only be used on fainted Pokémon";
            } else if (isPotionItem) {
              canUseItem = !fainted;
              disabledReason = fainted ? "Cannot use healing items on fainted Pokémon" : "";
            }
            
            return (
              <div 
                key={`${pokemon.slot}-${pokemon.current_hp}-${pokemon.max_hp}`}
                className={`pokemon-target-item ${!canUseItem ? 'disabled' : ''} ${fainted ? 'fainted' : ''}`}
                onClick={() => {
                  if (canUseItem) {
                    onItemUse(selectedItem, pokemon.slot);
                    setSelectedItem(null);
                    setSelectingTarget(false);
                  }
                }}
                style={{ 
                  pointerEvents: canUseItem ? 'auto' : 'none',
                  opacity: canUseItem ? 1 : 0.5
                }}
                title={disabledReason}
              >
                <img 
                  src={`/icon/${pokemon.name}.png`} 
                  alt={pokemon.name} 
                  className="pokemon-target-icon" 
                />
                <div className="pokemon-target-info">
                  <div>{pokemon.nickname || pokemon.name}</div>
                  <div className="pokemon-target-hp-bar">
                    <div 
                      className="pokemon-target-hp-fill" 
                      style={{ 
                        width: `${hpPercent}%`,
                        backgroundColor: hpColor
                      }}
                    />
                  </div>
                  <div className="pokemon-target-hp-text">
                    {pokemon.current_hp}/{pokemon.max_hp} HP
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        <button 
          className="back-button"
          onClick={() => {
            setSelectedItem(null);
            setSelectingTarget(false);
          }}
        >
          Back to Items
        </button>
      </div>
    );
  };

  // If there are no items with a quantity greater than 0, show message
  const hasItems = inventory.some(item => item.amount > 0);

  return (
    <div className="battle-inventory-panel">
      <div className="battle-inventory-header">
        <h4>{selectingTarget ? "Select Pokémon to use item on" : "Battle Items"}</h4>
        <button
          onClick={onClose}
          className="battle-inventory-close-btn"
        >
          ×
        </button>
      </div>
      
      <div className="battle-inventory-content">
        {selectingTarget ? (
          renderPokemonSelection()
        ) : (
          <div className="battle-inventory-items">
            {hasItems ? inventory.map(renderItem) : (
              <div style={{padding: "20px", textAlign: "center"}}>
                You don't have any usable items.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
